import type { IProcessActivity } from 'common/interfaces/IProcessActivity';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ServiceCode } from '../../../common/enums/ServiceCode';
import type { IQuestionnairesProgressItem } from '../../../common/interfaces/IQuestionnairesProgressItem';
import type { IReduxState } from '../../configureStore';
import { fetchJson } from '../../store/fetch';

export const useQuestionnairesProgress = (serviceCode: ServiceCode) => {
    const [questionnaireProcessActivities, setQuestionnaireProcessActivities] = useState<IProcessActivity[]>([]);
    const [questionnairesProgress, setQuestionnairesProgress] = useState<IQuestionnairesProgressItem[]>([]);

    const actions = useSelector((state: IReduxState) => state.authentication.actions);

    const availableQuestionnaireActions = useMemo(() => {
        return actions?.filter((action) => action.service.code === serviceCode) ?? [];
    }, [actions]);

    const completedQuestionnaires = useMemo(() => {
        return questionnairesProgress.filter((item) => item.status === 'complete');
    }, [questionnairesProgress]);

    useEffect(() => {
        getProcessActivities();
    }, []);

    useEffect(() => {
        getProcessQuestionnairesProgress();
    }, [questionnaireProcessActivities]);

    async function getProcessActivities() {
        const result = await fetchJson(`/api/patient/processActivities`, 'GET');

        if (result.json.items) {
            setQuestionnaireProcessActivities(result.json.items);
        }
    }

    async function getProcessQuestionnairesProgress() {
        if (questionnaireProcessActivities.length > 0) {
            const processActivitiesProcesses: string[] = questionnaireProcessActivities.map(
                (item: IProcessActivity) => item.processId
            );
            const processIds: string[] = [...new Set(processActivitiesProcesses)];

            for (const processId of processIds) {
                const result = await fetchJson(`/api/patient/process/${processId}/questionnairesProgress`, 'GET');

                if (result.json) {
                    setQuestionnairesProgress([...questionnairesProgress, ...result.json.items]);
                }
            }
        }
    }

    return {
        availableQuestionnaireActions,
        completedQuestionnaires,
        questionnaireProcessActivities,
        questionnairesProgress,
    };
};
